/**
 * The completion debrief: objectives, the lesson, and the itemised bill -
 * React edition of ui/WorkOrders.ts's showDebrief. Like the shift debrief it
 * borrows the bill from, presented without a verdict.
 */

import { Modal } from 'antd'
import { useMemo } from 'react'
import type { CSSProperties } from 'react'
import { t, tOr } from '../../i18n/i18n'
import type { Journal } from '../../learn/Journal'
import type { MissionProgress } from '../../learn/missionState'
import { callsMade, shiftCost } from '../../learn/shift'
import { missionDebriefText, missionObjectiveLabel, missionTitle } from '../../ui/WorkOrders'
import { useLang } from '../i18n/LanguageProvider'
import { CallsMade } from '../../ui/kit/CallsMade'

const costHeading: CSSProperties = {
  fontFamily: 'var(--mono, monospace)',
  fontSize: '10.5px',
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  color: 'var(--ink-muted)',
  margin: '10px 0 4px',
}

export function OrderDebrief({
  progress,
  journal,
  startIndex,
  onClose,
}: {
  progress: MissionProgress
  journal: Journal
  startIndex: number
  onClose: () => void
}) {
  useLang()
  const m = progress.mission
  // The bill is a fact about the moment the order completed, not a live feed.
  const cost = useMemo(() => shiftCost(journal.events.slice(startIndex)), [journal, startIndex])
  // The same slice, read for what the student BELIEVED rather than what it
  // cost. Also a fact about the moment the order completed.
  const calls = useMemo(() => callsMade(journal.events.slice(startIndex)), [journal, startIndex])
  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={`${tOr('orders.Done', 'Work order complete')} — ${missionTitle(m)}`}
      width="min(540px, 96vw)"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto' } }}
    >
      <div className="modal-body">
        {m.objectives.map((o, i) => (
          <div key={o.id} style={{ display: 'flex', gap: 8, margin: '3px 0' }}>
            <span style={{ color: '#4fae6d', flex: 'none' }}>✓</span>
            <span>{missionObjectiveLabel(m, i)}</span>
          </div>
        ))}
        <div style={{ margin: '12px 0', lineHeight: 1.6 }}>{missionDebriefText(m)}</div>
        <CallsMade calls={calls} />
        {cost.lines.length > 0 && (
          <>
            <div style={costHeading}>{tOr('orders.Cost', 'What it cost')}</div>
            {cost.lines.map((line, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, fontSize: '12.5px', margin: '2px 0' }}>
                <span style={{ flex: 1 }}>{line.label}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {`${line.hours.toFixed(1)} h`}
                </span>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                gap: 10,
                fontSize: '12.5px',
                margin: '6px 0 0',
                fontWeight: 600,
              }}
            >
              <span style={{ flex: 1 }}>{tOr('orders.TotalHours', 'Total tool time')}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {`${cost.totalHours.toFixed(1)} h`}
              </span>
            </div>
          </>
        )}
      </div>
      <div className="modal-actions">
        <button className="btn primary" onClick={onClose}>
          {t('panel.Close')}
        </button>
      </div>
    </Modal>
  )
}
